import { motion } from 'framer-motion';
import { Mountain, Award } from 'lucide-react';

// TODO: Connect to Supabase user_goals table
interface EverestProgress {
  progress: number;
  currentDay: number;
  totalDays: number;
  streak: number;
}

interface EverestProgressTileProps {
  everestProgress?: EverestProgress;
  onViewDetails?: () => void;
}

/**
 * Everest Progress Tile - Personal goal tracking
 * From FIGMA Clubhouse 2.0 design
 * 
 * @TODO Wire to useQuery for user_goals table
 */
export function EverestProgressTile({
  everestProgress,
  onViewDetails,
}: EverestProgressTileProps) {
  // Mock data - replace with real progress when connected to Supabase
  const defaultProgress: EverestProgress = {
    progress: 42,
    currentDay: 12,
    totalDays: 30,
    streak: 12,
  };

  const { progress, currentDay, totalDays, streak } = everestProgress || defaultProgress;

  return (
    <motion.div
      whileHover={{ y: -4, boxShadow: '0 20px 40px -10px rgba(0, 0, 0, 0.15)' }}
      transition={{ duration: 0.2 }}
      className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl shadow-md overflow-hidden h-full relative"
      data-figma-id="everest-progress-tile"
    >
      {/* Mountain Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <svg className="w-full h-full" viewBox="0 0 400 300" preserveAspectRatio="none">
          <path
            d="M0,150 L100,80 L150,100 L200,40 L250,70 L300,20 L350,60 L400,10 L400,300 L0,300 Z"
            fill="currentColor"
            className="text-white"
          />
        </svg>
      </div>

      <div className="relative px-6 py-5">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-white">Everest Challenge</h3>
          <Mountain className="w-5 h-5 text-slate-400" />
        </div>
        <p className="text-slate-300 text-sm">Your journey to mastery</p>
      </div>

      <div className="relative p-6 pt-0">
        {/* Progress Circle */}
        <div className="flex items-center justify-center mb-6">
          <div className="relative w-32 h-32">
            <svg className="w-full h-full transform -rotate-90">
              {/* Background circle */}
              <circle
                cx="64"
                cy="64"
                r="56"
                stroke="rgba(255,255,255,0.1)"
                strokeWidth="8"
                fill="none"
              />
              {/* Progress circle */}
              <motion.circle
                cx="64"
                cy="64"
                r="56"
                stroke="url(#gradient-everest)"
                strokeWidth="8"
                fill="none"
                strokeLinecap="round"
                initial={{ strokeDashoffset: 352 }}
                animate={{ strokeDashoffset: 352 - (352 * progress) / 100 }}
                transition={{ duration: 1, delay: 0.2, ease: 'easeOut' }}
                strokeDasharray="352"
              />
              <defs>
                <linearGradient id="gradient-everest" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#fbbf24" />
                  <stop offset="100%" stopColor="#f59e0b" />
                </linearGradient>
              </defs>
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-white text-sm">Day</span>
              <span className="text-amber-400 font-semibold">
                {currentDay}/{totalDays}
              </span>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div className="bg-white/10 rounded-xl p-3 backdrop-blur-sm">
            <div className="text-slate-400 text-xs mb-1">Progress</div>
            <div className="text-white font-semibold">{progress}%</div>
          </div>
          <div className="bg-white/10 rounded-xl p-3 backdrop-blur-sm">
            <div className="text-slate-400 text-xs mb-1">Streak</div>
            <div className="text-white flex items-center gap-1">
              <Award className="w-4 h-4 text-amber-400" />
              <span className="font-semibold">{streak} days</span>
            </div>
          </div>
        </div>

        <button
          onClick={onViewDetails}
          className="w-full px-4 py-3 bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white rounded-xl transition-colors border border-white/20"
        >
          View Challenge Details
        </button>
      </div>
    </motion.div>
  );
}

